/**
 * Persistent cart hook — stores in DB for logged-in users,
 * localStorage for anonymous visitors.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsAuth, pmsFrom } from '@/integrations/pms';
import type { CartItem } from '@/integrations/woo/types';

const LOCAL_CART_KEY = 'hydra_cart';

const getLocalCart = (): CartItem[] => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_CART_KEY) || '[]');
  } catch { return []; }
};

const setLocalCart = (items: CartItem[]) => {
  localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(items));
};

export const useCart = () => {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [cartId, setCartId] = useState<string | null>(null);

  useEffect(() => {
    pmsAuth.getSession().then((session) => {
      setUserId(session?.user?.id ?? null);
    });
    const unsubscribe = pmsAuth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => unsubscribe();
  }, []);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['cart', userId],
    queryFn: async (): Promise<CartItem[]> => {
      if (!userId) return getLocalCart();

      const { data: cart } = await pmsFrom('carts')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (cart) {
        setCartId(cart.id);
        const { data: cartItems } = await pmsFrom('cart_items')
          .select('*')
          .eq('cart_id', cart.id)
          .order('added_at');
        return (cartItems || []) as CartItem[];
      }
      return [];
    },
  });

  const ensureCart = useCallback(async () => {
    if (!userId) return null;
    if (cartId) return cartId;

    const { data } = await pmsFrom('carts')
      .insert({ user_id: userId, status: 'active' })
      .select('id')
      .single();

    if (data) {
      setCartId(data.id);
      return data.id;
    }
    return null;
  }, [userId, cartId]);

  const addItem = useMutation({
    mutationFn: async (item: Omit<CartItem, 'id'>) => {
      if (!userId) {
        const local = getLocalCart();
        const existing = local.find(i => i.product_id === item.product_id && i.variation_id === item.variation_id);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          local.push({ ...item, id: crypto.randomUUID() });
        }
        setLocalCart(local);
        return local;
      }

      const cid = await ensureCart();
      if (!cid) throw new Error('Failed to create cart');

      const { data: existing } = await pmsFrom('cart_items')
        .select('id, quantity')
        .eq('cart_id', cid)
        .eq('product_id', item.product_id)
        .maybeSingle();

      if (existing) {
        await pmsFrom('cart_items')
          .update({ quantity: existing.quantity + item.quantity })
          .eq('id', existing.id);
      } else {
        await pmsFrom('cart_items')
          .insert({ cart_id: cid, ...item });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] }),
  });

  const updateQuantity = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      if (!userId) {
        const local = getLocalCart();
        const item = local.find(i => i.id === itemId);
        if (item) {
          if (quantity <= 0) {
            setLocalCart(local.filter(i => i.id !== itemId));
          } else {
            item.quantity = quantity;
            setLocalCart(local);
          }
        }
        return;
      }

      if (quantity <= 0) {
        await pmsFrom('cart_items').delete().eq('id', itemId);
      } else {
        await pmsFrom('cart_items').update({ quantity }).eq('id', itemId);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] }),
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (!userId) {
        setLocalCart(getLocalCart().filter(i => i.id !== itemId));
        return;
      }
      await pmsFrom('cart_items').delete().eq('id', itemId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] }),
  });

  const clearCart = useMutation({
    mutationFn: async () => {
      if (!userId) {
        setLocalCart([]);
        return;
      }
      if (cartId) {
        await pmsFrom('cart_items').delete().eq('cart_id', cartId);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] }),
  });

  const getShareLink = useCallback(async (): Promise<string | null> => {
    if (!cartId) return null;
    const { data } = await pmsFrom('carts')
      .select('share_token')
      .eq('id', cartId)
      .single();
    if (data?.share_token) {
      return `${window.location.origin}/shop/shared-cart/${data.share_token}`;
    }
    return null;
  }, [cartId]);

  const total = items.reduce((sum, item) => sum + (item.product_price || 0) * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    items,
    isLoading,
    total,
    itemCount,
    addItem: addItem.mutate,
    updateQuantity: updateQuantity.mutate,
    removeItem: removeItem.mutate,
    clearCart: clearCart.mutate,
    getShareLink,
    isAdding: addItem.isPending,
  };
};
